// @ts-ignore
import { BigNumber, Contract, utils } from 'ethers'

// Constants
import WalletStakingPoolABI from '../../../constants/abis/WalletStakingPoolABI.json'
import WalletTokenABI from '../../../constants/abis/walletTokenABI.json'
// Types
import { Account } from '../../useAccounts'
import { ByHash, LogType, ParsedLogType } from './types'

const WALLET_TOKEN_ADDRESS = '0x88800092ff476844f74dc2fc427974bbee2794ae'
const WALLET_STAKING_ADDRESS = '0x47cd7e91c3cbaaf266369fe8518345fc4fc12935'

const ZERO = BigNumber.from(0)
const ZERO_ADDR = '0x0000000000000000000000000000000000000000'
const POOL_SHARES_TOKEN_DECIMALS_MUL = '1000000000000000000'
const STAKING_POOL_EVENT_TYPES = {
  enter: 'enter',
  leave: 'leave',
  burn: 'burn',
  withdraw: 'withdraw',
  rageLeave: 'rageLeave',
  shareTokensTransferIn: 'shareTokensTransferIn',
  shareTokensTransferOut: 'shareTokensTransferOut'
}

const getStats = async (accountId: Account['id'], provider: any) => {
  const ethProvider = provider

  const xWalletContract = new Contract(WALLET_STAKING_ADDRESS, WalletStakingPoolABI, ethProvider)
  const walletContract = new Contract(WALLET_TOKEN_ADDRESS, WalletTokenABI, ethProvider)
  const fromBlock = 0
  const [
    shareValue,
    sharesTotalSupply,
    balanceShares,
    lockedShares,
    allEnterWalletTransferLogs,
    leaveLogs,
    withdrawLogs,
    sharesTokensTransfersInLogs,
    sharesTokensTransfersOutLogs
  ] = await Promise.all([
    xWalletContract.shareValue(),
    xWalletContract.totalSupply(),
    xWalletContract.balanceOf(accountId),
    xWalletContract.lockedShares(accountId),
    ethProvider.getLogs({
      fromBlock,
      ...walletContract.filters.Transfer(null, WALLET_STAKING_ADDRESS, null)
    }),
    ethProvider.getLogs({
      fromBlock,
      ...xWalletContract.filters.LogLeave(accountId, null, null, null)
    }),
    ethProvider.getLogs({
      fromBlock,
      ...xWalletContract.filters.LogWithdraw(accountId, null, null, null, null)
    }),
    ethProvider.getLogs({
      fromBlock,
      ...xWalletContract.filters.LogRageLeave(accountId, null, null, null)
    }),
    ethProvider.getLogs({
      fromBlock,
      ...xWalletContract.filters.Transfer(null, accountId, null)
    }),
    ethProvider.getLogs({
      fromBlock,
      ...xWalletContract.filters.Transfer(accountId, null, null)
    })
  ])

  const enterWalletTokensByTxHash = []
    .concat(allEnterWalletTransferLogs)
    .reduce((byHash: ByHash, log: LogType) => {
      const byHashNew = { ...byHash }
      byHashNew[log.transactionHash] = log

      return byHashNew
    }, {})

  const sharesTokensTransfersIn = sharesTokensTransfersInLogs.map((log: LogType) => {
    const parsedLog: any = xWalletContract.interface.parseLog(log)

    const {
      from, // [0]
      amount // [2]
    } = parsedLog.args

    return {
      transactionHash: log.transactionHash,
      blockNumber: log.blockNumber,
      shares: amount,
      type:
        from === ZERO_ADDR
          ? STAKING_POOL_EVENT_TYPES.enter
          : STAKING_POOL_EVENT_TYPES.shareTokensTransferIn,
      from
    }
  })

  // Only out txns as we have logs for RageLEave and Withdraw and they only burns shares
  // TODO: detect innerBurn transactions to ZERO_ADDR (burned by the user itself)
  let sharesTokensTransfersOut = sharesTokensTransfersOutLogs
    .map((log: LogType) => {
      const parsedLog: any = xWalletContract.interface.parseLog(log)

      const {
        to, // [1]
        amount // [2]
      } = parsedLog.args

      return {
        transactionHash: log.transactionHash,
        blockNumber: log.blockNumber,
        shares: amount,
        type: STAKING_POOL_EVENT_TYPES.shareTokensTransferOut,
        to
      }
    })
    .filter((x: ParsedLogType) => x.to !== ZERO_ADDR)

  const { shareTokensTransfersInByTxHash } = sharesTokensTransfersIn.reduce(
    (txns: any, event: any) => {
      const txnsNew = { ...txns }
      if (event.type === STAKING_POOL_EVENT_TYPES.enter) {
        txnsNew.shareTokensEnterMintByHash[event.transactionHash] = event
      }

      if (event.type === STAKING_POOL_EVENT_TYPES.shareTokensTransferIn) {
        txnsNew.shareTokensTransfersInByTxHash[event.transactionHash] = event
      }

      return txnsNew
    },
    {
      shareTokensEnterMintByHash: {},
      shareTokensTransfersInByTxHash: {}
    }
  )

  let sharesTokensTransfersInFromExternal: Array<any> = Object.values(
    shareTokensTransfersInByTxHash
  )

  const userWithdraws = withdrawLogs.map((log: LogType) => {
    const parsedWithdrawLog: any = xWalletContract.interface.parseLog(log)
    const { shares, unlocksAt, maxTokens, receivedTokens } = parsedWithdrawLog.args

    return {
      transactionHash: log.transactionHash,
      type: STAKING_POOL_EVENT_TYPES.withdraw,
      shares, // [1]
      unlocksAt, // [2]
      maxTokens, // [3]
      receivedTokens, // [4]
      blockNumber: log.blockNumber
    }
  })

  const now = new Date().getTime() / 1000

  const userLeaves = await Promise.all(
    leaveLogs.map(async (log: LogType) => {
      const parsedLog: any = xWalletContract.interface.parseLog(log)

      const { shares, unlocksAt, maxTokens } = parsedLog.args

      const withdrawTx = userWithdraws.find(
        (event: any) =>
          event.unlocksAt.toString() === unlocksAt.toString() &&
          event.shares.toString() === shares.toString() &&
          event.maxTokens.toString() === maxTokens.toString()
      )

      const walletValue = sharesTotalSupply.isZero()
        ? ZERO // maxTokens
        : await xWalletContract.unbondingCommitmentWorth(accountId, shares, unlocksAt)

      return {
        transactionHash: log.transactionHash,
        type: STAKING_POOL_EVENT_TYPES.leave,
        shares, // [1]
        unlocksAt, // [2]
        maxTokens, // [3]
        walletValue,
        canWithdraw: unlocksAt < now && !withdrawTx,
        blockNumber: log.blockNumber,
        withdrawTx
      }
    })
  )

  const leavesPendingToUnlock = [...userLeaves].filter((event) => event.unlocksAt > now)

  const leavesReadyToWithdraw = [...userLeaves].filter(
    (event) => event.unlocksAt < now && !event.withdrawTx
  )

  const leavesPendingToUnlockTotalWallet = leavesPendingToUnlock.reduce(
    (a, b) => a.add(b.walletValue),
    ZERO
  )

  const leavesReadyToWithdrawTotalWallet = leavesReadyToWithdraw.reduce(
    (a, b) => a.add(b.walletValue),
    ZERO
  )

  let leavePendingToUnlockOrReadyToWithdraw = null
  if (leavesReadyToWithdraw.length) leavePendingToUnlockOrReadyToWithdraw = leavesReadyToWithdraw[0]
  else if (leavesPendingToUnlock.length)
    leavePendingToUnlockOrReadyToWithdraw = leavesPendingToUnlock[0]
  const [latestLog] = leaveLogs.sort((a: LogType, b: LogType) => b.blockNumber - a.blockNumber)
  let remainingTime
  if (leavePendingToUnlockOrReadyToWithdraw && latestLog) {
    const { unlocksAt } = leavePendingToUnlockOrReadyToWithdraw
    remainingTime = unlocksAt.toString() * 1000 - Date.now()
    if (remainingTime <= 0) remainingTime = 0
  } else {
    remainingTime = null
  }

  if (sharesTokensTransfersOut.length || sharesTokensTransfersInFromExternal.length) {
    const fromBlockNotZero = Math.min(
      sharesTokensTransfersOut[0]
        ? sharesTokensTransfersOut[0].blockNumber
        : Number.MAX_SAFE_INTEGER,
      sharesTokensTransfersInFromExternal[0]
        ? sharesTokensTransfersInFromExternal[0].blockNumber
        : Number.MAX_SAFE_INTEGER
    )

    const [allLeaveLogs, allWithdrawLogs, allRageLeaveLogs, allEnterSharesTokensTransfersInLogs] =
      await Promise.all([
        ethProvider.getLogs({
          fromBlockNotZero,
          ...xWalletContract.filters.LogLeave(null, null, null, null)
        }),
        ethProvider.getLogs({
          fromBlockNotZero,
          ...xWalletContract.filters.LogWithdraw(null, null, null, null, null)
        }),
        ethProvider.getLogs({
          fromBlockNotZero,
          ...xWalletContract.filters.LogRageLeave(null, null, null, null)
        }),
        ethProvider.getLogs({
          fromBlockNotZero,
          ...xWalletContract.filters.Transfer(ZERO_ADDR, null, null)
        })
      ])

    const allEnters = allEnterSharesTokensTransfersInLogs
      .map((sharesMintEvent: any) => {
        const walletTokenTransfersLog = enterWalletTokensByTxHash[sharesMintEvent.transactionHash]

        if (walletTokenTransfersLog) {
          const parsedLog: any = walletContract.interface.parseLog(walletTokenTransfersLog)
          const amount = parsedLog.args.amount
          const parsedSharesMint: any = xWalletContract.interface.parseLog(sharesMintEvent)
          const { amount: shares } = parsedSharesMint.args

          return {
            blockNumber: sharesMintEvent.blockNumber,
            shareValue: shares.isZero()
              ? ZERO
              : amount.mul(POOL_SHARES_TOKEN_DECIMALS_MUL).div(shares)
          }
        }
        return null
      })
      .filter(Boolean)

    const allWithdraws = allWithdrawLogs.map((log: LogType) => {
      const parsedWithdrawLog: any = xWalletContract.interface.parseLog(log)
      const { shares, maxTokens } = parsedWithdrawLog.args

      return {
        blockNumber: log.blockNumber,
        shareValue: shares.isZero()
          ? ZERO
          : maxTokens.mul(POOL_SHARES_TOKEN_DECIMALS_MUL).div(shares)
      }
    })

    const allRageLeaves = allRageLeaveLogs.map((log: LogType) => {
      const parsedRageLeaveLog: any = xWalletContract.interface.parseLog(log)

      const { shares, maxTokens } = parsedRageLeaveLog.args

      return {
        shareValue: shares.isZero()
          ? ZERO
          : maxTokens.mul(POOL_SHARES_TOKEN_DECIMALS_MUL).div(shares),
        blockNumber: log.blockNumber
      }
    })

    const allLeaves = allLeaveLogs.map((log: LogType) => {
      const parsedLog: any = xWalletContract.interface.parseLog(log)
      const { shares, maxTokens } = parsedLog.args
      return {
        blockNumber: log.blockNumber,
        shareValue: shares.isZero()
          ? ZERO
          : maxTokens.mul(POOL_SHARES_TOKEN_DECIMALS_MUL).div(shares)
      }
    })

    const allLogs = allEnters
      .concat(allWithdraws)
      .concat(allLeaves)
      .concat(allRageLeaves)
      .sort((a: LogType, b: LogType) => a.blockNumber - b.blockNumber)

    const withWalletAmount = (events: any) =>
      events.map((transferLog: any, i: number) => {
        const newEvents = [...events]
        const nextLog =
          allLogs.find((log: LogType) => log.blockNumber >= transferLog.blockNumber) || {}

        const bestShareValue = nextLog.shareValue || shareValue

        // approximate share value
        newEvents[i].shareValue = bestShareValue
        newEvents[i].walletAmount = transferLog.shares
          .mul(bestShareValue)
          .div(POOL_SHARES_TOKEN_DECIMALS_MUL)

        return newEvents[i]
      })

    sharesTokensTransfersOut = withWalletAmount(sharesTokensTransfersOut)
    sharesTokensTransfersInFromExternal = withWalletAmount(sharesTokensTransfersInFromExternal)
  }

  const totalLockedSharesCheck = [...userLeaves]
    .filter((x) => !x.withdrawTx)
    .reduce((a, b) => a.add(b.shares), ZERO)

  if (!totalLockedSharesCheck.eq(lockedShares)) {
    // eslint-disable-next-line no-console
    console.error(
      'locked shares different than check sum, user balance can be incorrect',
      'lockedShares:',
      lockedShares.toString(),
      'totalLockedSharesCheck:',
      totalLockedSharesCheck.toString()
    )
  }

  const balanceSharesAvailable = balanceShares.sub(lockedShares).lt(ZERO)
    ? ZERO
    : balanceShares.sub(lockedShares)

  const currentBalanceWalletAvailable = balanceSharesAvailable
    .mul(shareValue)
    .div(POOL_SHARES_TOKEN_DECIMALS_MUL)

  return (
    Number(utils.formatUnits(currentBalanceWalletAvailable.toString(), 18)) +
    Number(utils.formatUnits(leavesPendingToUnlockTotalWallet.toString(), 18)) +
    Number(utils.formatUnits(leavesReadyToWithdrawTotalWallet.toString(), 18))
  )
}

export default getStats
